import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, X } from 'lucide-react';
import { toast } from 'sonner';

import PdfExamViewer, { applyWatermark } from '../PdfExamViewer';
import { useAuth } from '../../context/AuthContext';
import { studentExamApi } from '../../api/examApi';

// Legacy online corrected papers may be JPG/PNG. They are drawn onto a canvas
// with the same watermark overlay as PDF pages, so they are also view-only.
function WatermarkedImage({ src, watermark }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      applyWatermark(ctx, canvas, watermark);
    };
    img.src = src;
  }, [src, watermark]);
  return (
    <canvas
      ref={canvasRef}
      className="block w-full rounded border border-slate-200 shadow-sm dark:border-slate-700"
      style={{ userSelect: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}

/**
 * View-only corrected paper. Students never get a download control for this
 * file. The watermark (student name + id[:8] + view timestamp) is drawn
 * client-side only; server-side stamping is out of scope.
 */
export function CorrectedPaperViewer({ attemptId, className = 'max-h-[70vh]' }) {
  const { user } = useAuth();
  const [blobUrl, setBlobUrl] = useState(null);
  const [kind, setKind] = useState(null); // 'pdf' | 'image'
  const [error, setError] = useState(false);
  // One timestamp per viewing session, so the watermark identity is stable.
  const [viewedAt] = useState(() => new Date().toLocaleString());

  useEffect(() => {
    let revoked = false;
    let url = null;
    setError(false);
    setBlobUrl(null);
    studentExamApi
      .downloadCorrectedPaper(attemptId)
      .then((res) => {
        if (revoked) return;
        const blob = res.data;
        url = URL.createObjectURL(blob);
        setKind(blob.type && blob.type.startsWith('image/') ? 'image' : 'pdf');
        setBlobUrl(url);
      })
      .catch(() => {
        if (!revoked) setError(true);
      });
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attemptId]);

  const watermark = useMemo(
    () => ({ name: user?.name || 'Student', id: String(user?.id || ''), timestamp: viewedAt }),
    [user?.name, user?.id, viewedAt]
  );

  if (error) return <p className="text-sm text-red-500">Could not load the corrected paper.</p>;
  if (!blobUrl) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading corrected paper…</p>;
  return kind === 'image' ? (
    <div className={`overflow-y-auto ${className}`}>
      <WatermarkedImage src={blobUrl} watermark={watermark} />
    </div>
  ) : (
    <PdfExamViewer src={blobUrl} title="Corrected Paper" watermark={watermark} className={className} />
  );
}

// Button + modal, for list pages that previously offered a download.
export function ViewCorrectedPaperButton({ attemptId, label = 'View Corrected Paper', className = '' }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (!attemptId) return toast.error('Corrected paper unavailable');
          setOpen(true);
        }}
        className={
          className ||
          'inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/40'
        }
      >
        <Eye size={12} /> {label}
      </button>
      {open && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Corrected paper"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white p-4 shadow-xl dark:bg-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-white">Corrected Paper</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X size={18} />
              </button>
            </div>
            <CorrectedPaperViewer attemptId={attemptId} className="min-h-0 flex-1 max-h-[75vh]" />
          </div>
        </div>,
        // Portal: an ancestor's animation transform would otherwise become the
        // containing block for `fixed` and clip the dialog under the top bar.
        document.body
      )}
    </>
  );
}

export default CorrectedPaperViewer;
